import { format } from 'date-fns';
import { DASH } from '../utils/displayValues';
import {
  LOGO_DATA_URL,
  formatDateOfBirthWithAge,
  formatFullName,
  formatGender,
  type TemplatePatient,
} from './templateUtils';

export interface MedicationsDigitalTemplateRow {
  id: string;
  name?: string | null;
  dosageValue?: string;
  dosageUnit?: string;
  dosageForm?: string;
  diagnosis?: string;
  prescriberFirstName?: string;
  prescriberLastName?: string;
  prescriberPracticeName?: string;
  prescriberNpi?: string;
  status?: string;
  writtenDate?: string;
  lastFillDate?: string;
  quantityValue?: string;
  quantityUnit?: string;
  daysSupply?: number;
  refillsRemaining?: number;
  prescriberOrderNumber?: string;
  priorAuthNumber?: string;
  diagnosisQualifier?: string;
  sources?: Array<{ qualifier: string; description: string }>;
}

export interface MedicationsDigitalTemplateProps {
  medications: MedicationsDigitalTemplateRow[];
  patient?: TemplatePatient;
  generatedAt?: Date;
  pageInfo?: { current: number; total: number };
  filename?: string;
  highlightMedicationId?: string;
}

const DISCLAIMER =
  "Data provided by Surescripts. Disclaimer: This report may not include certain sensitive information due to the requirements of federal, state and/or local privacy laws and regulations, such as details related to reproductive health, gender-affirming care, sexually transmitted infections (STIs/HIV), mental health, and substance use treatment. It may also omit over-the-counter medications, certain prescriptions paid for by the patient or non-participating sources, or any information the patient has chosen to keep private not to share. Additionaly, discrepancies can occur, such as false positive matches in the Master Patient Index. The provider should verify the patient's demographics and medication history with the patient for accuracy.";

const HeaderCell = ({ label }: { label: string }) => (
  <th className="border-r border-[#DDDDDD] bg-[#EDEDED] px-3 py-2 text-left text-[11px] leading-tight font-bold [overflow-wrap:anywhere] text-[#171717] last:border-r-0">
    {label}
  </th>
);

const DataCell = ({
  value,
  className,
}: {
  value?: React.ReactNode;
  className?: string;
}) => (
  <td
    className={`border-r border-[#DDDDDD] bg-[#F9FAFB] px-3 py-2 align-top text-[11px] leading-tight [overflow-wrap:anywhere] text-[#171717] last:border-r-0 ${className ?? ''}`}
  >
    {value ?? DASH}
  </td>
);

const MedicationCard = ({
  med,
  highlightMedicationId,
}: {
  med: MedicationsDigitalTemplateRow;
  highlightMedicationId?: string;
}) => {
  const isHighlighted = med.id === highlightMedicationId;
  const prescriberName = [med.prescriberFirstName, med.prescriberLastName]
    .filter(Boolean)
    .join(' ');

  const quantity = [med.quantityValue, med.quantityUnit]
    .filter(Boolean)
    .join(' ');
  const supply =
    med.daysSupply != null ? `${med.daysSupply} day supply` : undefined;
  const source = med.sources?.[0]?.description;

  const dosageCell = (
    <span className="flex flex-col gap-0.5">
      {med.dosageValue != null && (
        <span>
          <span className="font-semibold">Value:</span> {med.dosageValue}
        </span>
      )}
      {med.dosageUnit && (
        <span>
          <span className="font-semibold">Unit:</span> {med.dosageUnit}
        </span>
      )}
      {med.dosageForm && (
        <span>
          <span className="font-semibold">Form:</span> {med.dosageForm}
        </span>
      )}
      {med.dosageValue == null && !med.dosageUnit && !med.dosageForm && DASH}
    </span>
  );

  const prescriberCell = (
    <span className="flex flex-col gap-0.5">
      {prescriberName && (
        <span className="font-semibold">{prescriberName}</span>
      )}
      {med.prescriberPracticeName && <span>{med.prescriberPracticeName}</span>}
      {med.prescriberNpi && <span>NPI# {med.prescriberNpi}</span>}
      {!prescriberName &&
        !med.prescriberPracticeName &&
        !med.prescriberNpi &&
        DASH}
    </span>
  );

  return (
    <div
      id={`medication-${med.id}`}
      tabIndex={isHighlighted ? -1 : undefined}
      className={`overflow-hidden rounded border ${isHighlighted ? 'border-amber-400' : 'border-[#DDDDDD]'}`}
    >
      <div
        className={`border-b border-[#DDDDDD] px-4 py-3 ${isHighlighted ? 'bg-amber-100' : 'bg-[#EDEDED]'}`}
      >
        <p className="text-[14px] leading-tight font-bold text-[#171717]">
          {med.name?.trim() || DASH}
        </p>
      </div>

      {/* Section 1: Dosage 14% | Diagnosis 28% | Prescriber 19% | Status 14% | Date Written 11% | Last Fill Date 14% */}
      <table className="w-full table-fixed border-collapse border-b border-[#DDDDDD]">
        <colgroup>
          <col style={{ width: '14%' }} />
          <col style={{ width: '28%' }} />
          <col style={{ width: '19%' }} />
          <col style={{ width: '14%' }} />
          <col style={{ width: '11%' }} />
          <col style={{ width: '14%' }} />
        </colgroup>
        <thead>
          <tr className="border-b border-[#DDDDDD]">
            <HeaderCell label="Dosage" />
            <HeaderCell label="Diagnosis" />
            <HeaderCell label="Prescriber" />
            <HeaderCell label="Status" />
            <HeaderCell label="Date Written" />
            <HeaderCell label="Last Fill Date" />
          </tr>
        </thead>
        <tbody>
          <tr>
            <DataCell value={dosageCell} />
            <DataCell value={med.diagnosis} />
            <DataCell value={prescriberCell} />
            <DataCell value={med.status} />
            <DataCell value={med.writtenDate} />
            <DataCell value={med.lastFillDate} />
          </tr>
        </tbody>
      </table>

      {/* Section 2: Quantity 14% | Supply 14% | Refills 14% | Source 19% | Order# 14% | Prior Auth 11% | Diag Qualifier 14% */}
      {/* Supply+Refills=28%=Diagnosis | Source=Prescriber | Order#=Status | Prior Auth=Date Written | Diag Qualifier=Last Fill Date */}
      <table className="w-full table-fixed border-collapse">
        <colgroup>
          <col style={{ width: '14%' }} />
          <col style={{ width: '14%' }} />
          <col style={{ width: '14%' }} />
          <col style={{ width: '19%' }} />
          <col style={{ width: '14%' }} />
          <col style={{ width: '11%' }} />
          <col style={{ width: '14%' }} />
        </colgroup>
        <thead>
          <tr className="border-b border-[#DDDDDD]">
            <HeaderCell label="Quantity" />
            <HeaderCell label="Supply" />
            <HeaderCell label="Refills Remaining" />
            <HeaderCell label="Source" />
            <HeaderCell label="Prescriber Order #" />
            <HeaderCell label="Prior Auth #" />
            <HeaderCell label="Diagnosis Qualifier" />
          </tr>
        </thead>
        <tbody>
          <tr>
            <DataCell value={quantity || undefined} />
            <DataCell value={supply} />
            <DataCell value={med.refillsRemaining?.toString()} />
            <DataCell value={source} />
            <DataCell value={med.prescriberOrderNumber} />
            <DataCell value={med.priorAuthNumber} />
            <DataCell value={med.diagnosisQualifier} />
          </tr>
        </tbody>
      </table>
    </div>
  );
};

export const MedicationsDigitalTemplate = ({
  medications,
  patient,
  generatedAt,
  pageInfo,
  highlightMedicationId,
}: MedicationsDigitalTemplateProps) => {
  const items = medications ?? [];
  const generated = generatedAt ?? new Date();

  return (
    <div className="mx-auto flex min-h-[1056px] w-[816px] flex-col bg-white font-sans text-black">
      {/* Title header */}
      <div className="flex items-center justify-between border-b border-zinc-300 px-8 pt-8 pb-5">
        <h1 className="text-[28px] leading-tight font-bold text-black">
          Medications
        </h1>
        <div className="flex items-center gap-1.5">
          <span className="text-[11px] text-zinc-500">Powered by</span>
          <img src={LOGO_DATA_URL} alt="Predoc" className="h-5 w-auto" />
        </div>
      </div>

      {/* Patient info */}
      {patient && (
        <section className="px-8 pt-5">
          <div className="flex items-baseline gap-3 rounded border border-zinc-300 bg-zinc-100 px-4 py-3">
            <span className="text-[15px] leading-tight font-bold text-black">
              {formatFullName(patient)}
            </span>
            <span className="text-[13px] leading-tight text-zinc-600">
              {[
                patient.dateOfBirth &&
                  `DOB: ${formatDateOfBirthWithAge(patient.dateOfBirth)}`,
                formatGender(patient.genderName),
              ]
                .filter(Boolean)
                .join(' • ')}
            </span>
          </div>
        </section>
      )}

      {/* Content */}
      <section className="flex flex-1 flex-col gap-5 px-8 py-5">
        <p className="text-[20px] leading-tight font-bold text-black">
          Medications
        </p>

        {items.length === 0 ? (
          <div className="rounded border-2 border-gray-200 bg-white px-5 py-12 text-center text-[12px] text-zinc-500">
            No medications available
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            {items.map((med, idx) => (
              <MedicationCard
                key={med.id ?? `med-${idx}`}
                med={med}
                highlightMedicationId={highlightMedicationId}
              />
            ))}
          </div>
        )}

        <p className="text-[9px] leading-normal text-[#525252]">{DISCLAIMER}</p>
      </section>

      {/* Footer */}
      <footer className="mt-auto flex items-center justify-between border-t border-zinc-300 px-8 py-4">
        <p className="text-[10px] leading-tight text-zinc-700">
          Confidential medical information. Generated{' '}
          {format(generated, 'MM/dd/yyyy')}. Powered by Predoc.
        </p>
        {pageInfo && (
          <p className="text-[10px] leading-tight text-zinc-700">
            {pageInfo.current}/{pageInfo.total}
          </p>
        )}
      </footer>
    </div>
  );
};
